import React, { useEffect, useRef } from 'react';
import {
  CANVAS_W, CANVAS_H, PLAYER_X, PLAYER, PLAYER_SPRITE, BIRD_SPRITE, POKEMON_IDS,
  freshState, startJump, endJump, step, hits, drawScene, mustDuck, pokemonSprite,
} from './PokeDash';

// Cheeky Dash playing itself, for the Arcade card. It runs the real game engine with a simple auto-player, and only
// animates while it's on screen. It skips the slow start and plays the busy part: fast enough for every bird, the
// rolling Pokémon and the legendaries, and starts that over every so often so it never settles into top speed
const RESTART_AFTER_MS = 30000;
const PREVIEW_SPEED = 10;  // the game starts at 6; birds come from 8.5 and rolling Pokémon from 9.5

const busyStart = () => {
  const s = freshState();
  s.speed = PREVIEW_SPEED;
  s.runTime = 2400;  // obstacles almost straight away
  return s;
};

// The auto-player: jump balls, rolling Pokémon and ground-skimming birds; duck the low and bouncing ones
function autoPlay(s) {
  const next = s.obstacles.find(o => o.x + o.w > PLAYER_X);
  const dist = next ? next.x - (PLAYER_X + PLAYER.w) : Infinity;
  // Duck only just before a bird arrives, and get up as soon as it's gone by
  if (next && mustDuck(next) && dist < s.speed * 2 + 12) {
    s.duckHeld = true;
    if (s.jumping) { s.speedDrop = true; s.vy = Math.max(s.vy, 1); }  // in the air: drop fast, as the Duck button does
    else s.ducking = true;
    return;
  }
  if (s.ducking || s.duckHeld) { s.ducking = false; s.duckHeld = false; }
  if (!next) return;
  const lead = s.speed * 5 + 4; // react a few frames before it arrives
  if (!mustDuck(next) && dist < lead && dist > -5 && !s.jumping) {
    s.ducking = false; s.duckHeld = false;
    startJump(s);
    s.released = Math.random() < 0.35; // sometimes a short hop, like a real player
    if (s.released) endJump(s);
  }
}

export default function DashPreview() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const sprites = { pokemon: {} };
    const load = (key, src) => { const img = new Image(); img.onload = () => { sprites[key] = img; }; img.src = src; };
    load('player', PLAYER_SPRITE);
    load('bird', BIRD_SPRITE);
    POKEMON_IDS.forEach((id) => {
      const img = new Image();
      img.onload = () => { sprites.pokemon[id] = img; };
      img.src = pokemonSprite(BIRD_SPRITE, id);
    });

    let s = busyStart();
    let started = performance.now();
    let last = null;
    let raf = null;
    let visible = false;
    const still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const frame = (now) => {
      raf = null;
      if (last != null) {
        const dt = Math.min(now - last, 50);
        autoPlay(s);
        step(s, dt);
        if (hits(s) || now - started > RESTART_AFTER_MS) {
          s = busyStart(); started = now;
        }
      }
      last = now;
      drawScene(ctx, s, sprites, now, true);
      if (visible && !still && !document.hidden) raf = requestAnimationFrame(frame);
    };
    const resume = () => {
      if (raf == null && visible && !document.hidden) { last = null; raf = requestAnimationFrame(frame); }
    };

    // Only animate while the card is on screen and the tab is open
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) resume();
    });
    observer.observe(canvas);
    const onVisibility = () => resume();
    document.addEventListener('visibilitychange', onVisibility);
    drawScene(ctx, s, sprites, performance.now(), true); // a first frame straight away
    // Redraw once the sprites have loaded, in case the card is showing a still frame
    const spriteTimer = setTimeout(() => drawScene(ctx, s, sprites, performance.now(), true), 800);

    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimeout(spriteTimer);
      if (raf != null) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      width={CANVAS_W}
      height={CANVAS_H}
      aria-label="Cheeky Dash playing itself"
      role="img"
      className="block w-full h-full"
      style={{ objectFit: 'cover', imageRendering: 'pixelated' }}
    />
  );
}
